/**
 * Difficulty director — Stage 1b.
 *
 * Owns the time-based difficulty ramp. Per GAME_DESIGN.md the ramp is a
 * DUAL-speed ramp: BOTH the player's forward speed (v_p) AND the oncoming
 * traffic speed (v_veh) increase over `TUNING.director.rampDuration`, so the
 * closing speed grows from both sides. Density and truck ratio interpolate
 * with progress too (both are flat at the current tuning).
 *
 * All ramps are linear in progress `p = clamp(time / rampDuration, 0, 1)`.
 * The director is a pure function of time — no internal mutable state — so it
 * is trivially deterministic and safe to query from any point in a run.
 */

import { TUNING } from "./tuning";

/** Traffic absolute speeds for each vehicle kind at a given progress. */
export interface TrafficSpeeds {
  car: number;
  truck: number;
}

/** Read-only view of director outputs handed to the spawner each step. */
export interface DirectorState {
  /**
   * Sim time since the run started (s). Unlike `progress` this is NOT clamped,
   * so the spawner can gate pattern unlocks on absolute run time.
   */
  time: number;
  /** Ramp progress in [0, 1]. */
  progress: number;
  /** Player forward speed v_p (m/s). */
  playerSpeed: number;
  /** Oncoming traffic absolute speeds (m/s) per kind. */
  trafficSpeeds: TrafficSpeeds;
  /** Threat density: expected vehicles per 100 m of corridor. */
  density: number;
  /** Probability a newly spawned threat is a truck (vs car). */
  truckRatio: number;
}

export interface Director {
  /**
   * Snapshot all difficulty parameters at a given sim time.
   * Pure: identical inputs => identical output.
   */
  at(time: number): DirectorState;
  /** Convenience: ramp progress in [0,1]. */
  progress(time: number): number;
  /** Convenience: player forward speed (m/s) at time. */
  playerSpeed(time: number): number;
  /** Convenience: traffic absolute speeds at time. */
  trafficSpeeds(time: number): TrafficSpeeds;
  /** Convenience: threat density (per 100 m) at time. */
  density(time: number): number;
  /** Convenience: truck ratio in [0,1] at time. */
  truckRatio(time: number): number;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * Create a difficulty director. The director holds no mutable runtime state;
 * the factory exists so callers get a stable typed surface matching the
 * `Director` interface.
 */
export function createDirector(): Director {
  const rampDuration = TUNING.director.rampDuration;

  function progress(time: number): number {
    if (rampDuration <= 0) return 1;
    const p = time / rampDuration;
    if (p < 0) return 0;
    if (p > 1) return 1;
    return p;
  }

  function playerSpeed(time: number): number {
    const p = progress(time);
    return lerp(TUNING.player.speedStart, TUNING.player.speedMax, p);
  }

  function trafficSpeeds(time: number): TrafficSpeeds {
    const p = progress(time);
    return {
      car: lerp(TUNING.traffic.carSpeedStart, TUNING.traffic.carSpeedMax, p),
      truck: lerp(TUNING.traffic.truckSpeedStart, TUNING.traffic.truckSpeedMax, p),
    };
  }

  function density(time: number): number {
    const p = progress(time);
    return lerp(TUNING.traffic.densityStart, TUNING.traffic.densityMax, p);
  }

  function truckRatio(time: number): number {
    const p = progress(time);
    return lerp(TUNING.traffic.truckRatioStart, TUNING.traffic.truckRatioMax, p);
  }

  function at(time: number): DirectorState {
    return {
      time,
      progress: progress(time),
      playerSpeed: playerSpeed(time),
      trafficSpeeds: trafficSpeeds(time),
      density: density(time),
      truckRatio: truckRatio(time),
    };
  }

  return {
    at,
    progress,
    playerSpeed,
    trafficSpeeds,
    density,
    truckRatio,
  };
}
